import React, { useEffect, useState } from "react";
import { CalendarDays, Clock, Users, Video } from "lucide-react";
import ScheduleClient from "../../interviewSchedule/data/api/ScheduleClient";
import Loader from "../../../shared/components/Loader";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "../../../shared/components/Table/Table";
import { InterviewSchedule } from "../../interviewSchedule/data/interfaces/ScheduleInterfaces";

interface JobInterviewsTabProps {
  jobOpportunityId: string;
}

const normalise = (r: any): InterviewSchedule => ({
  id: r.id || r.schedule_id || '',
  sessionName: r.session_name || r.sessionName || '',
  roundType: r.round_type || r.roundType || '-',
  round_id: r.round_id || '',
  startDateTime: r.start_datetime || r.startDateTime || '',
  endDateTime: r.end_datetime || r.endDateTime || '',
  videoRecording: r.is_video_needed ?? r.videoRecording ?? false,
  duration: r.duration || 0,
  status: (r.status || 'upcoming').toLowerCase() as InterviewSchedule['status'],
  assignedUsers: r.target_users
    ? (r.target_users as string[]).map((email: string) => ({ id: email, name: email, email, role: '', department: '' }))
    : r.assignedUsers || [],
  target_users: r.target_users || [],
  createdAt: r.created_at || r.createdAt || '',
  interviewType: (r.interview_type || 'AI') as 'AI' | 'EXPERT',
  interviewerEmail: r.interviewer_email || r.interviewerEmail || '',
  meetingLink: r.meeting_link || r.meetingLink || '',
  jobDescription: r.job_description || r.jobDescription || '',
});

const JobInterviewsTab: React.FC<JobInterviewsTabProps> = ({ jobOpportunityId }) => {
  const [schedules, setSchedules] = useState<InterviewSchedule[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSchedules = async () => {
      setLoading(true);
      try {
        const offset = new Date().getTimezoneOffset();
        const sign = offset > 0 ? '-' : '+';
        const abs = Math.abs(offset);
        const offsetTime = `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;

        const data = await ScheduleClient.list({
          job_opportunity_id: jobOpportunityId,
          offset_time: offsetTime,
          limit: 100
        });
        
        const rawRecords = Array.isArray(data) ? data : data?.records || [];
        setSchedules(rawRecords.map(normalise));
      } catch (err) {
        console.error("Failed to load schedules", err);
      } finally {
        setLoading(false);
      }
    };
    if (jobOpportunityId) {
      fetchSchedules();
    }
  }, [jobOpportunityId]);

  const formatDateTime = (raw: string) => {
    if (!raw) return '-';
    const iso = raw.replace(/\//g, '-').replace(', ', 'T');
    const d = new Date(iso);
    if (isNaN(d.getTime())) return raw;
    return (
      d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ', ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    );
  };

  if (loading) return <div style={{ padding: '40px' }}><Loader isLoading={true} message="Loading interviews..." /></div>;

  if (schedules.length === 0) {
    return (
      <div className="jo-empty-tab">
        <CalendarDays size={48} color="#cbd5e1" />
        <h3>No Interviews Scheduled</h3>
        <p>There are no interviews scheduled for this job opportunity yet.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Table className="learner-table">
        <TableHeader>
          <TableRow>
            <TableHead>Session Name</TableHead>
            <TableHead>Round Type</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Start Date & Time</TableHead>
            <TableHead>Participants</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {schedules.map(schedule => (
            <TableRow key={schedule.id}>
              <TableCell>
                <strong style={{ color: '#0f172a' }}>{schedule.sessionName || '-'}</strong>
              </TableCell>
              <TableCell>
                <div style={{ display: 'flex', alignItems: 'center', color: '#475569' }}>
                  {schedule.roundType || '-'}
                </div>
              </TableCell>
              <TableCell>
                <span className={`is-type-badge ${schedule.interviewType === 'EXPERT' ? 'expert' : 'ai'}`}>
                  {schedule.interviewType === 'EXPERT' ? 'Expert' : 'AI'}
                </span>
              </TableCell>
              <TableCell>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                  <Clock size={14} color="#94a3b8" />
                  {formatDateTime(schedule.startDateTime)}
                </div>
              </TableCell>
              <TableCell>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                  <Users size={14} color="#94a3b8" />
                  {schedule.target_users?.length ?? schedule.assignedUsers?.length ?? 0}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default JobInterviewsTab;
